import type { ReviewListItem, ReviewSummary } from "@shared/types"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ReviewStars } from "./review-stars"

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

export function ReviewSummarySection({
  title,
  summary,
  reviews,
  emptyMessage = "No reviews yet.",
  loadFailed,
}: {
  title: string
  summary: ReviewSummary
  reviews: ReviewListItem[]
  emptyMessage?: string
  /**
   * Set when the reviews could not be fetched. The section then says so and
   * offers a retry, instead of reporting a rating and a count it never read.
   */
  loadFailed?: { message: string; retryLabel: string; onRetry: () => void }
}): React.ReactElement {
  if (loadFailed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{loadFailed.message}</CardDescription>
        </CardHeader>
        <CardContent>
          <button
            type="button"
            className="text-sm font-medium underline"
            onClick={loadFailed.onRetry}
          >
            {loadFailed.retryLabel}
          </button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {summary.reviewCount > 0 && (
          <CardDescription>
            {`${summary.averageRating?.toFixed(1) || "0.0"} average rating from ${summary.reviewCount} review${summary.reviewCount === 1 ? "" : "s"}`}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {summary.reviewCount > 0 && (
          <div className="flex items-center gap-3">
            <ReviewStars rating={summary.averageRating} />
            <span className="text-sm text-muted-foreground">
              {summary.reviewCount} review{summary.reviewCount === 1 ? "" : "s"}
            </span>
          </div>
        )}

        {reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <div key={review.id} className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {review.reviewerDisplayName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(review.updatedAt)}
                    </p>
                  </div>
                  <ReviewStars rating={review.rating} />
                </div>
                {review.comment ? (
                  <p className="mt-2 text-sm text-muted-foreground">{review.comment}</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
